// Shared materials editor — used by both production and sampling quotes.
import React from "react";
import { Trash2, Plus } from "lucide-react";

export function newMaterial() {
  return { id: Math.random().toString(36).slice(2, 10), name: "", quantity: "", price: "" };
}

export function materialsSubtotal(materials = []) {
  return materials.reduce(
    (sum, m) => sum + Number(m.quantity || 0) * Number(m.price || 0),
    0,
  );
}

export function MaterialsEditor({ materials, onChange, sectionTitle = "Materials" }) {
  const mu = (id, patch) => onChange(materials.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const mremove = (id) => onChange(materials.filter((m) => m.id !== id));
  const add = () => onChange([...materials, newMaterial()]);

  return (
    <div>
      <div className="sg-section-h">{sectionTitle}</div>
      {materials.length === 0 && (
        <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 12 }}>
          No materials added yet.
        </div>
      )}
      {materials.map((m, idx) => {
        const lineTotal = Number(m.quantity || 0) * Number(m.price || 0);
        return (
          <div key={m.id} style={cardStyle} data-testid={`mat-${idx}`}>
            <div style={rowHead}>
              <span style={itemNo}>Material #{idx + 1}</span>
              <button onClick={() => mremove(m.id)} style={removeBtn} data-testid={`mat-remove-${idx}`}>
                <Trash2 size={14} color="var(--danger)" />
              </button>
            </div>
            <label className="sg-label">Material name</label>
            <input
              className="sg-input"
              value={m.name || ""}
              onChange={(e) => mu(m.id, { name: e.target.value })}
              placeholder="e.g. Cotton fabric"
              data-testid={`mat-name-${idx}`}
            />
            <div style={row2}>
              <div style={col}>
                <label className="sg-label">Quantity</label>
                <input
                  className="sg-input"
                  value={m.quantity || ""}
                  onChange={(e) => mu(m.id, { quantity: e.target.value })}
                  inputMode="numeric"
                  data-testid={`mat-qty-${idx}`}
                />
              </div>
              <div style={col}>
                <label className="sg-label">Price ₹ / unit</label>
                <input
                  className="sg-input"
                  value={m.price || ""}
                  onChange={(e) => mu(m.id, { price: e.target.value })}
                  inputMode="numeric"
                  data-testid={`mat-price-${idx}`}
                />
              </div>
            </div>
            <div style={lineTotalRow}>
              <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase" }}>
                Line total
              </span>
              <span style={{ fontSize: 14, color: "var(--ink)", fontWeight: 700 }}>
                ₹{Number(lineTotal || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        );
      })}
      <button type="button" onClick={add} style={addBtn} data-testid="mat-add">
        <Plus size={16} color="var(--brand)" />
        <span style={{ color: "var(--brand)", fontWeight: 700 }}>Add material</span>
      </button>
    </div>
  );
}

const cardStyle = { background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 12, padding: 14, marginBottom: 12 };
const rowHead = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 };
const itemNo = { fontSize: 11, fontWeight: 800, color: "var(--brand)", textTransform: "uppercase", letterSpacing: 0.6 };
const removeBtn = { width: 30, height: 30, borderRadius: 8, background: "var(--surface-subtle)", border: 0, cursor: "pointer", display: "grid", placeItems: "center" };
const row2 = { display: "flex", gap: 8 };
const col = { flex: 1 };
const addBtn = { display: "flex", alignItems: "center", gap: 6, justifyContent: "center", width: "100%", padding: 12, border: "1px dashed var(--line)", borderRadius: 10, background: "var(--surface)", cursor: "pointer", marginBottom: 14 };
const lineTotalRow = { display: "flex", justifyContent: "space-between", paddingTop: 8, marginTop: 4, borderTop: "1px solid var(--line)" };
